/**
 * スクロールリビール演出（GSAP）
 *
 * - prefers-reduced-motion が true なら全演出スキップ（要素は通常表示のまま）
 * - gsap.from のみ使用（JS 失敗時も要素が見える状態を維持・FOUC 防止）
 * - .p-hero__copy とその子要素には一切触らない（LCP 保護）
 * - .p-hero__visual / .p-hero__circle のみヒーロー演出を適用
 */
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

function initScrollReveal(): void {
  // prefers-reduced-motion が有効なら全演出スキップ
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  // ─────────────────────────────────────────────
  // ヒーロービジュアル（FV・ロード時）
  // LCP 対象の .p-hero__copy には触れない。
  // ビジュアルブロックのみ、ページロード直後に軽く出現させる。
  // ─────────────────────────────────────────────
  const heroVisual = document.querySelector<HTMLElement>(".p-hero__visual");
  if (heroVisual) {
    gsap.from(heroVisual, {
      opacity: 0,
      y: 20,
      scale: 0.97,
      duration: 0.8,
      delay: 0.1,
      ease: "power2.out",
    });
  }

  // 装飾円：ごく控えめな浮遊ループ（各円に少しずつ delay を変えて自然に）
  const circles = document.querySelectorAll<HTMLElement>(".p-hero__circle");
  const circleDelays = [0, 0.6, 1.2];
  circles.forEach((circle, i) => {
    gsap.to(circle, {
      y: -8,
      duration: 3.5 + i * 0.4,
      delay: circleDelays[i] ?? i * 0.6,
      ease: "sine.inOut",
      yoyo: true,
      repeat: -1,
    });
  });

  // ─────────────────────────────────────────────
  // スクロールリビール共通設定
  // ─────────────────────────────────────────────
  const FADE_DEFAULTS = {
    opacity: 0,
    y: 24,
    duration: 0.7,
    ease: "power2.out",
  } as const;

  /** 単要素のフェードアップ */
  function revealEl(el: Element): void {
    gsap.from(el, {
      ...FADE_DEFAULTS,
      scrollTrigger: {
        trigger: el,
        start: "top 85%",
        once: true,
      },
    });
  }

  /** 複数要素のスタガー */
  function revealStagger(els: NodeListOf<Element> | Element[]): void {
    if (!els.length) return;
    gsap.from(els, {
      ...FADE_DEFAULTS,
      stagger: 0.12,
      scrollTrigger: {
        trigger: els[0],
        start: "top 85%",
        once: true,
      },
    });
  }

  // ─────────────────────────────────────────────
  // セクション見出し（.c-section-heading）
  // ─────────────────────────────────────────────
  document.querySelectorAll<HTMLElement>(".c-section-heading").forEach(revealEl);

  // ─────────────────────────────────────────────
  // パッケージカード（stagger）
  // ─────────────────────────────────────────────
  revealStagger(document.querySelectorAll(".p-package__card"));

  // ─────────────────────────────────────────────
  // For You カード（stagger）
  // ─────────────────────────────────────────────
  revealStagger(document.querySelectorAll(".p-foryou__card"));

  // ─────────────────────────────────────────────
  // 実績カード（stagger）
  // ─────────────────────────────────────────────
  revealStagger(document.querySelectorAll(".p-works__card"));

  // ─────────────────────────────────────────────
  // ブログカード（stagger）
  // ─────────────────────────────────────────────
  revealStagger(document.querySelectorAll(".p-blog-list__card"));

  // ─────────────────────────────────────────────
  // About 行
  // ─────────────────────────────────────────────
  const aboutRow = document.querySelector(".p-about__row");
  if (aboutRow) revealEl(aboutRow);

  // ─────────────────────────────────────────────
  // CTA バンド内の中身
  // ─────────────────────────────────────────────
  const ctaBand = document.querySelector(".p-cta-band");
  if (ctaBand) revealEl(ctaBand);
}

// DOMContentLoaded 後に起動
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initScrollReveal);
} else {
  initScrollReveal();
}
